import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import TextDiff from '../../test-sets/TextDiff';
import { SwitchInput } from '../../common/SwitchInput';

export function fuseboxToText({fuses, templateId, templateRenameMap}) {
  return fuses.map(({id, kind, description}) => {
    let remappedId = templateRenameMap ? templateRenameMap[id] || id : id;
    return `[${remappedId}] ${kind?.amp || '-'} A - ${description}. `;
  }).sort().join('\n');
}

function toDiffText(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

function getRemappedId(fuse, templateRenameMap) {
  return templateRenameMap ? templateRenameMap[fuse.id] || fuse.id : fuse.id;
}

function fuseMap(fuses, templateRenameMap) {
  const map = {};
  for (const fuse of fuses || []) {
    map[getRemappedId(fuse, templateRenameMap)] = fuse;
  }
  return map;
}

function normalizeValue(value, normalizeStrings) {
  const text = toDiffText(value);
  if (!normalizeStrings) {
    return text;
  }
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ +/g, ' ').toLowerCase();
}

function fuseAmp(fuse) {
  const amp = fuse?.kind?.amp;
  if (amp === null || amp === undefined || amp === '') {
    return '-';
  }
  return toDiffText(amp);
}

function fuseDescription(fuse) {
  return toDiffText(fuse?.description);
}

function fuseIdSortKey(id) {
  return toDiffText(id).replace(/\d+/g, digits => digits.padStart(4, '0'));
}

function buildFuseComparisonRows(boxA, boxB, normalizeStrings) {
  const mapA = fuseMap(boxA.fuses, boxA.templateRenameMap);
  const mapB = fuseMap(boxB.fuses, boxB.templateRenameMap);
  const allIds = _.sortBy(_.uniq([...Object.keys(mapA), ...Object.keys(mapB)]), fuseIdSortKey);

  return allIds.map(id => {
    const fuseA = mapA[id];
    const fuseB = mapB[id];

    if (fuseA && fuseB) {
      const ampA = fuseAmp(fuseA);
      const ampB = fuseAmp(fuseB);
      const descA = fuseDescription(fuseA);
      const descB = fuseDescription(fuseB);
      const ampChanged = normalizeValue(ampA, normalizeStrings) !== normalizeValue(ampB, normalizeStrings);
      const descChanged = normalizeValue(descA, normalizeStrings) !== normalizeValue(descB, normalizeStrings);

      return {
        id,
        type: 'matched',
        ampA,
        ampB,
        descA,
        descB,
        ampChanged,
        descChanged,
        displayAmpA: normalizeStrings ? normalizeValue(ampA, true) : ampA,
        displayAmpB: normalizeStrings ? normalizeValue(ampB, true) : ampB,
        displayDescA: normalizeStrings ? normalizeValue(descA, true) : descA,
        displayDescB: normalizeStrings ? normalizeValue(descB, true) : descB,
      };
    }

    if (fuseA) {
      return {
        id,
        type: 'removed',
        amp: fuseAmp(fuseA),
        description: fuseDescription(fuseA),
      };
    }

    return {
      id,
      type: 'added',
      amp: fuseAmp(fuseB),
      description: fuseDescription(fuseB),
    };
  });
}

function FuseComparisonTable({rows}) {
  return <table className={'table table-sm table-bordered fusebox-box-comparison-table mb-0 bg-white'}>
    <thead>
      <tr>
        <th className={'fuse-id-cell'}>ID</th>
        <th className={'fuse-amp-cell'}>Amp (A)</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(row => {
        if (row.type === 'removed') {
          return <tr key={row.id} className={'fuse-row-removed'}>
            <td className={'fuse-id-cell'}><del>{row.id}</del></td>
            <td className={'fuse-amp-cell'}><del>{row.amp}</del></td>
            <td><del>{row.description}</del></td>
          </tr>;
        }

        if (row.type === 'added') {
          return <tr key={row.id} className={'fuse-row-added'}>
            <td className={'fuse-id-cell'}><ins>{row.id}</ins></td>
            <td className={'fuse-amp-cell'}><ins>{row.amp}</ins></td>
            <td><ins>{row.description}</ins></td>
          </tr>;
        }

        const changed = row.ampChanged || row.descChanged;

        return <tr key={row.id}>
          <td className={'fuse-id-cell'}>{row.id}</td>
          <td className={`fuse-amp-cell${row.ampChanged ? ' cell-changed' : ''}`}>
            {row.ampChanged ?
              <TextDiff inputA={row.displayAmpA} inputB={row.displayAmpB} type={'words'}/> :
              row.ampA}
          </td>
          <td className={row.descChanged ? 'cell-changed' : ''}>
            {row.descChanged ?
              <TextDiff inputA={row.displayDescA} inputB={row.displayDescB} type={'words'} joinWordChanges={true}/> :
              row.descA}
          </td>
        </tr>;
      })}
    </tbody>
  </table>;
}

export default function FuseboxBoxComparison({boxA, boxB, labelA, labelB, normalizeStrings = false}) {
  const rows = useMemo(
    () => buildFuseComparisonRows(boxA, boxB, normalizeStrings),
    [boxA, boxB, normalizeStrings]
  );

  if (!boxA || !boxB) {
    return <div className={'alert alert-danger'}>Missing box to compare</div>;
  }

  const boxTitle = labelA && labelB
    ? `${labelA} vs ${labelB}`
    : 'Box';

  return <div>
    <h5 className={'mt-2'}>
      {boxTitle} (<TextDiff inputA={toDiffText(boxA.boxName)} inputB={toDiffText(boxB.boxName)} type={'words'}/>)
    </h5>
    Templated id: <TextDiff inputA={toDiffText(boxA.templateId)} joinWordChanges={true} inputB={toDiffText(boxB.templateId)} type={'sentences'}/>
    <div className={'mt-2 mb-1'}>Fuses:</div>
    <FuseComparisonTable rows={rows}/>
  </div>;
}

export function FuseboxBoxComparisonPanel({boxA, boxB, labelA, labelB}) {
  let [normalizeStrings, setNormalize] = useState(false);

  return <div className='bg-light p-2'>
    <div className={'mb-2'}>
      <SwitchInput value={normalizeStrings} onChange={v => setNormalize(v)}>Ignore accents and case</SwitchInput>
    </div>
    <FuseboxBoxComparison boxA={boxA}
                          boxB={boxB}
                          labelA={labelA}
                          labelB={labelB}
                          normalizeStrings={normalizeStrings}/>
  </div>;
}
